import React, { useEffect, useState } from "react"
import { Alert, Box, Card, CardContent, Grid, LinearProgress, Stack, Typography } from "@mui/material"
import { collection, documentId, getDocs, query, where } from "firebase/firestore"

import { db } from "../../firebase"
import { WheelPrizeFormData } from "./wheelPrizeTypes"

const getTodayDayKey = () => new Date().toISOString().slice(0, 10)

type SpinDocShape = {
  prizeSnapshot?: { type?: string; label?: string }
  lastPrizeId?: string
  lastLabel?: string
}

const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
    <CardContent>
      <Typography variant="h4">{value}</Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
)

// Reads directly from the same Firestore collections the wheel already writes
// (`wheel-spins`, `wheel-grand-prize-counters`, `wheel-prize-counters`) rather than a
// separate analytics backend — this app has no charting/analytics service to plug into.
export const WheelAnalytics: React.FC<{ prizes: WheelPrizeFormData[] }> = ({ prizes }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [spinsToday, setSpinsToday] = useState(0)
  const [grandPrizesToday, setGrandPrizesToday] = useState(0)
  const [distribution, setDistribution] = useState<Record<string, number>>({})
  const [inventory, setInventory] = useState<Record<string, { total?: number; daily?: number }>>({})

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const dayKey = getTodayDayKey()
        const spinsSnap = await getDocs(query(collection(db, "wheel-spins"), where("dayKey", "==", dayKey)))
        if (cancelled) return

        const dist: Record<string, number> = {}
        spinsSnap.docs.forEach((docSnap) => {
          const data = docSnap.data() as SpinDocShape
          const label = data.prizeSnapshot?.label ?? data.lastLabel ?? data.lastPrizeId ?? "Unknown"
          dist[label] = (dist[label] ?? 0) + 1
        })
        setDistribution(dist)
        setSpinsToday(spinsSnap.size)

        const grandSnap = await getDocs(collection(db, "wheel-grand-prize-counters"))
        const todayGrand = grandSnap.docs.find((docSnap) => docSnap.id === dayKey)
        setGrandPrizesToday((todayGrand?.data() as any)?.count ?? 0)

        const cappedPrizes = prizes.filter((prize) => prize.totalQuantity || prize.quantityPerDay)
        const nextInventory: Record<string, { total?: number; daily?: number }> = {}
        await Promise.all(
          cappedPrizes.map(async (prize) => {
            const counters = await getDocs(
              query(
                collection(db, "wheel-prize-counters"),
                where(documentId(), "in", [prize.id, `${prize.id}_daily_${dayKey}`]),
              ),
            )
            let total: number | undefined
            let daily: number | undefined
            counters.docs.forEach((docSnap) => {
              const count = (docSnap.data() as any)?.count ?? 0
              if (docSnap.id === prize.id) total = count
              else daily = count
            })
            nextInventory[prize.id] = { total, daily }
          }),
        )
        if (!cancelled) setInventory(nextInventory)
      } catch (err: any) {
        console.error("Error loading wheel analytics:", err)
        if (!cancelled) setError(err?.message ?? "Failed to load analytics")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [prizes])

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <StatCard label="Spins today" value={spinsToday} />
        <StatCard label="Unique participants today" value={spinsToday} />
        <StatCard label="Grand prizes awarded today" value={grandPrizesToday} />
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Prize distribution today
          </Typography>
          {Object.keys(distribution).length === 0 ? (
            <Typography color="text.secondary">No spins yet today.</Typography>
          ) : (
            <Stack spacing={1}>
              {Object.entries(distribution)
                .sort((a, b) => b[1] - a[1])
                .map(([label, count]) => (
                  <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {count}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {Object.keys(inventory).length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Remaining inventory
            </Typography>
            <Grid container spacing={2}>
              {prizes
                .filter((prize) => inventory[prize.id])
                .map((prize) => {
                  const counts = inventory[prize.id]
                  return (
                    <Grid item xs={12} sm={6} key={prize.id}>
                      <Typography variant="body2" fontWeight={600}>
                        {prize.label}
                      </Typography>
                      {prize.totalQuantity && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lifetime: {counts.total ?? 0} / {prize.totalQuantity}
                        </Typography>
                      )}
                      {prize.quantityPerDay && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Today: {counts.daily ?? 0} / {prize.quantityPerDay}
                        </Typography>
                      )}
                    </Grid>
                  )
                })}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Stack>
  )
}

export default WheelAnalytics
